import React from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";

import IconButton from "./IconButton";
import { cn } from "./cn";

type Props = {
  page: number;
  pageSize: number;
  total: number | null | undefined;
  onChange: (next: { page: number; pageSize: number }) => void;
  pageSizeOptions?: number[];
  className?: string;
};

/**
 * Brand-styled replacement for the legacy Pagination component. Emits a
 * single {page, pageSize} event so callers don't track two pieces of state
 * through two setters.
 */
const Pagination: React.FC<Props> = ({
  page,
  pageSize,
  total,
  onChange,
  pageSizeOptions = [10, 25, 50, 100],
  className,
}) => {
  const safeTotal = typeof total === "number" && total >= 0 ? total : 0;
  const totalPages = Math.max(1, Math.ceil(safeTotal / Math.max(1, pageSize)));
  const current = Math.min(Math.max(1, page), totalPages);
  const firstItem = safeTotal === 0 ? 0 : (current - 1) * pageSize + 1;
  const lastItem = Math.min(current * pageSize, safeTotal);

  const go = (next: number) =>
    onChange({ page: Math.min(Math.max(1, next), totalPages), pageSize });

  return (
    <div
      className={cn(
        "flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <p className="text-[11.5px] text-gray-500">
        {safeTotal === 0 ? (
          "No results"
        ) : (
          <>
            Showing{" "}
            <span className="font-semibold text-gray-700">
              {firstItem.toLocaleString()}–{lastItem.toLocaleString()}
            </span>{" "}
            of <span className="font-semibold text-gray-700">{safeTotal.toLocaleString()}</span>
          </>
        )}
      </p>

      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 text-[11.5px] text-gray-500">
          Rows
          <select
            value={pageSize}
            onChange={(e) =>
              onChange({ page: 1, pageSize: Number(e.target.value) })
            }
            className="h-7 rounded-md border border-gray-200 bg-white pl-2 pr-1 text-[11.5px] text-gray-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <div className="inline-flex items-center gap-1">
          <IconButton
            aria-label="Previous page"
            variant="secondary"
            size="sm"
            disabled={current <= 1}
            onClick={() => go(current - 1)}
          >
            <HiChevronLeft className="h-4 w-4" />
          </IconButton>
          <span className="inline-flex h-8 min-w-[4rem] items-center justify-center rounded-md bg-gray-50 px-2 text-[11.5px] font-semibold tabular-nums text-gray-700">
            {current} / {totalPages}
          </span>
          <IconButton
            aria-label="Next page"
            variant="secondary"
            size="sm"
            disabled={current >= totalPages}
            onClick={() => go(current + 1)}
          >
            <HiChevronRight className="h-4 w-4" />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
